import { lazy, Suspense } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import PageTransition from "./PageTransition";
import { PageSkeleton, DashboardSkeleton, AnalyticsSkeleton, FormSkeleton } from "./ui/skeleton-loaders";

const Index = lazy(() => import("@/pages/Index"));
const SystemDetail = lazy(() => import("@/pages/SystemDetail"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Auth = lazy(() => import("@/pages/Auth"));
const Admin = lazy(() => import("@/pages/Admin"));
const AdminConsole = lazy(() => import("@/pages/AdminConsole"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const CaseStudies = lazy(() => import("@/pages/CaseStudies"));
const AIAnalytics = lazy(() => import("@/pages/AIAnalytics"));
const CampaignAnalytics = lazy(() => import("@/pages/CampaignAnalytics"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const Terms = lazy(() => import("@/pages/Terms"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const SolutionPage = lazy(() => import("@/pages/SolutionPage"));
const SolutionsIndex = lazy(() => import("@/pages/SolutionsIndex"));
const OAuthConsent = lazy(() => import("@/pages/OAuthConsent"));

const AnimatedRoutes = () => {
  const location = useLocation();

  const getSkeleton = (path: string) => {
    if (path.includes("/dashboard")) return <DashboardSkeleton />;
    if (path.includes("/analytics") || path.includes("/ai-analytics")) return <AnalyticsSkeleton />;
    if (path.includes("/auth")) return <FormSkeleton />;
    return <PageSkeleton />;
  };

  return (
    <AnimatePresence mode="wait">
      <Suspense fallback={getSkeleton(location.pathname)}>
        <Routes location={location} key={location.pathname}>
          <Route
            path="/"
            element={
              <PageTransition>
                <Index />
              </PageTransition>
            }
          />
          <Route
            path="/systems/:slug"
            element={
              <PageTransition>
                <SystemDetail />
              </PageTransition>
            }
          />
          <Route
            path="/auth"
            element={
              <PageTransition>
                <Auth />
              </PageTransition>
            }
          />
          <Route
            path="/admin"
            element={
              <PageTransition>
                <Admin />
              </PageTransition>
            }
          />
          <Route
            path="/admin/console"
            element={
              <PageTransition>
                <AdminConsole />
              </PageTransition>
            }
          />
          <Route
            path="/dashboard"
            element={
              <PageTransition>
                <Dashboard />
              </PageTransition>
            }
          />
          <Route
            path="/dashboard/campaign/:id"
            element={
              <PageTransition>
                <CampaignAnalytics />
              </PageTransition>
            }
          />
          <Route
            path="/ai-analytics"
            element={
              <PageTransition>
                <AIAnalytics />
              </PageTransition>
            }
          />
          <Route
            path="/case-studies"
            element={
              <PageTransition>
                <CaseStudies />
              </PageTransition>
            }
          />
          <Route
            path="/privacy"
            element={
              <PageTransition>
                <PrivacyPolicy />
              </PageTransition>
            }
          />
          <Route
            path="/terms"
            element={
              <PageTransition>
                <Terms />
              </PageTransition>
            }
          />
          <Route
            path="/reset-password"
            element={
              <PageTransition>
                <ResetPassword />
              </PageTransition>
            }
          />
          <Route
            path="/solutions"
            element={
              <PageTransition>
                <SolutionsIndex />
              </PageTransition>
            }
          />
          <Route
            path="/solutions/:slug"
            element={
              <PageTransition>
                <SolutionPage />
              </PageTransition>
            }
          />
          <Route
            path="/.lovable/oauth/consent"
            element={<OAuthConsent />}
          />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route
            path="*"
            element={
              <PageTransition>
                <NotFound />
              </PageTransition>
            }
          />
        </Routes>
      </Suspense>
    </AnimatePresence>
  );
};

export default AnimatedRoutes;
